import { useEffect, useState } from 'react';
import { Typography, Tabs, Tab, Stack, Box, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTabs } from 'hooks/useTabs';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { WeatherAlertList } from 'features/weatherAlerts/components/WeatherAlertList';
import { DeliverySettings } from './DeliverySettings';
import { IncidentsSubscriptionsEditor } from './IncidentsSubscriptionsEditor';

const TAB_PANEL_ID_PREFIX = 'notification-settings';

const useStyles = makeStyles()((theme) => ({
  container: {
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    width: '100%',
  },
}));

export const NotificationsSubscriptionsEditor = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [, setIsMounted] = useState(false);
  const isCriticalAlertsEnabled = useReleaseFlag('enable_critical_alerts');

  useEffect(() => {
    // For component to re-render when navigating back to this page and
    // container element is not null so we know if component's scrollable.
    setIsMounted(true);
  }, []);

  const tabData = [
    {
      tabId: 'incidents',
      label: t('notifications.tabs.incidents'),
      enabled: true,
      content: <IncidentsSubscriptionsEditor />,
      panelSx: { flex: 1, mt: '0px !important' },
    },
    {
      tabId: 'other',
      label: t('notifications.tabs.other'),
      enabled: true,
      content: <WeatherAlertList fromRoute="/notifications?tab=other" />,
      panelSx: { flex: 1, mt: '0px !important' },
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: 'notifications tabs',
    tabData,
  });

  return (
    <Box className={classes.container}>
      <Stack sx={{ width: '100%' }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          {isCriticalAlertsEnabled ? (
            <DeliverySettings />
          ) : (
            <Typography variant="h2">
              {t('notifications.notifications')}
            </Typography>
          )}

          <Button
            fullWidth
            size="large"
            component={Link}
            to={{ pathname: '/notifications/test' }}
            color="neutral"
          >
            {t('notifications.test')}
          </Button>
        </Stack>

        {isCriticalAlertsEnabled ? (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="h3">
              {t('notifications.subscriptions')}
            </Typography>
          </Box>
        ) : null}

        <Tabs {...tabsProps}>
          {tabProps.map((props) => {
            // 'key' prop should not be spread into JSX
            const { key, ...rest } = props;
            return <Tab key={key} {...rest} />;
          })}
        </Tabs>

        {tabPanels}
      </Stack>
    </Box>
  );
};
